/**
 * `goodsReceipts.*` wire contract (Phase G2). Receive against a PO; posting
 * flips the PO status and drafts a matching bill. Paise cross the wire as
 * stringified bigint; quantities are operator decimal strings.
 */
import { z } from "zod";
import { uuid, paged, isoDate, moneyPaise } from "../_common.js";

export const grnStatus = z.enum(["draft", "posted", "void"]);

const qtyString = z
  .string()
  .trim()
  .regex(/^\d+(\.\d{0,3})?$/, "quantity must be a non-negative number");

export const recordLineInput = z.object({
  purchaseOrderLineId: uuid,
  receivedQty: qtyString,
  acceptedQty: qtyString,
  rejectReason: z.string().trim().max(500).nullable().optional(),
});

/** Shared field set for record + createDraft (the draft path ignores `approvalId`). */
const recordBaseInput = z.object({
  purchaseOrderId: uuid,
  receivedDate: isoDate,
  vehicleChallan: z.string().trim().max(64).nullable().optional(),
  receivedBy: z.string().trim().max(120).nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
  /** Destination warehouse. Null = fall back to company default. */
  warehouseId: uuid.nullable().optional(),
  lines: z.array(recordLineInput).min(1).max(200),
});

export const recordInput = recordBaseInput.extend({
  /**
   * Resubmit token (ADR-0031). When a configured `goods_receipt` chain gated
   * the post, the operator approves in the inbox and resends with the
   * `pending_approvals.id` here; the domain verifies the payload still matches
   * and bypasses the gate.
   */
  approvalId: uuid.optional(),
});

export const recordOutput = z.object({
  /** The posted GRN id; null while awaiting approval (nothing inserted yet). */
  id: uuid.nullable(),
  /** `posted` = number allocated + PO flipped; `awaiting_approval` = chain pending. */
  outcome: z.enum(["posted", "awaiting_approval"]),
  grnNumber: z.string().nullable(),
  billId: uuid.nullable(),
  poStatus: z.string().nullable(),
  /** Filed `pending_approvals.id` when `outcome='awaiting_approval'`, else null. */
  approvalId: uuid.nullable(),
});

export const getInput = z.object({ id: uuid });

/** `createDraft` saves the same payload as `record`, just unposted (no gate). */
export const createDraftInput = recordBaseInput;
export const createDraftOutput = z.object({ id: uuid });

export const updateInput = z.object({
  id: uuid,
  receivedDate: isoDate,
  vehicleChallan: z.string().trim().max(64).nullable().optional(),
  receivedBy: z.string().trim().max(120).nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
  warehouseId: uuid.nullable().optional(),
  lines: z.array(recordLineInput).min(1).max(200),
});
export const updateOutput = z.object({ id: uuid });

/** Shared {id} input for deleteDraft + post. */
export const idInput = z.object({ id: uuid });
export const deleteDraftOutput = z.object({ id: uuid });

/**
 * Posting a saved draft GRN. Distinct from `recordOutput` (the one-shot, which
 * carries the ADR-0031 approval outcome) — the draft-post path is not yet gated,
 * so it always returns a posted GRN.
 */
export const postOutput = z.object({
  id: uuid,
  grnNumber: z.string(),
  billId: uuid.nullable(),
  poStatus: z.string(),
});

export const voidInput = z.object({
  id: uuid,
  reason: z.string().trim().max(500).nullable().optional(),
});
export const voidOutput = z.object({
  id: uuid,
  status: grnStatus,
  poStatus: z.string(),
  voidedBillId: uuid.nullable(),
});

export const grnLineDetail = z.object({
  id: uuid,
  position: z.number().int().positive(),
  purchaseOrderLineId: uuid,
  description: z.string(),
  hsn: z.string(),
  unit: z.string(),
  orderedQtyMilli: z.number().int().nonnegative(),
  receivedQtyMilli: z.number().int().nonnegative(),
  acceptedQtyMilli: z.number().int().nonnegative(),
  rejectedQtyMilli: z.number().int().nonnegative(),
  rejectReason: z.string().nullable(),
  ratePaise: moneyPaise,
  acceptedValuePaise: moneyPaise,
});

export const grnDetail = z.object({
  id: uuid,
  grnNumber: z.string().nullable(),
  purchaseOrderId: uuid,
  poNumber: z.string().nullable(),
  vendorId: uuid,
  vendorName: z.string(),
  vendorGstin: z.string().nullable(),
  vendorCity: z.string().nullable(),
  vendorState: z.string().nullable(),
  receivedDate: isoDate,
  vehicleChallan: z.string().nullable(),
  receivedBy: z.string().nullable(),
  notes: z.string().nullable(),
  status: grnStatus,
  billId: uuid.nullable(),
  warehouseId: uuid.nullable(),
  acceptedValuePaise: moneyPaise,
  lineCount: z.number().int().nonnegative(),
  rejectedLineCount: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
  createdByActorId: z.string(),
  createdByActorType: z.string(),
  lines: z.array(grnLineDetail),
});

export const getOutput = grnDetail;

export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  status: grnStatus.optional(),
  vendorId: uuid.optional(),
  purchaseOrderId: uuid.optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
  search: z.string().trim().min(1).max(80).optional(),
});

export const listOutput = paged(
  z.object({
    id: uuid,
    grnNumber: z.string().nullable(),
    purchaseOrderId: uuid,
    poNumber: z.string().nullable(),
    vendorId: uuid,
    vendorName: z.string(),
    vendorCity: z.string().nullable(),
    receivedDate: isoDate,
    status: grnStatus,
    acceptedValuePaise: moneyPaise,
    lineCount: z.number().int().nonnegative(),
    rejectedLineCount: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
  }),
).extend({
  counts: z.object({
    all: z.number().int().nonnegative(),
    draft: z.number().int().nonnegative(),
    posted: z.number().int().nonnegative(),
    void: z.number().int().nonnegative(),
  }),
  kpis: z.object({
    postedCount: z.number().int().nonnegative(),
    acceptedValuePaise: moneyPaise,
    receiptsThisMonth: z.number().int().nonnegative(),
    receiptsWithRejections: z.number().int().nonnegative(),
    topVendorName: z.string().nullable(),
    topVendorReceiptCount: z.number().int().nonnegative(),
  }),
});
